import React, { useState, useEffect, useContext } from "react";
import { UserContext } from "../context/UserContext";
import axios from "axios";
import Select from "react-select";

const Search = ({ onClose, onSearchResults }) => {
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedBill, setSelectedBill] = useState(null);
  const [bills, setBills] = useState([]); // Dynamically fetched bills
  const [searchResults, setSearchResults] = useState(null); // Store fetched results for display
  const { phoneNo } = useContext(UserContext);

  const categories = [
    "Affordable housing",
    "Better business conditions",
    "Better education",
    "Crime reduction & law enforcement",
    "Defence and security",
    "Economic stability",
    "Environmental and Energy Issues",
    "Fair workplaces",
    "Family and children's needs",
    "Health and hospitals",
    "Honest government",
    "Increasing migration",
    "Lower taxes for families",
    "Rural needs",
    "Reducing unemployment",
    "Reducing migration",
    "Support for elderly",
  ];

  // Format categories for react-select
  const categoryOptions = categories.map((category) => ({
    value: category,
    label: category,
  }));

  // Fetch bill titles from API
  useEffect(() => {
    const fetchBills = async () => {
      try {
        const response = await axios.get("/api/federal-bills");
        if (response.status === 200) {
          const formattedBills = response.data.map((bill) => ({
            value: bill.title || bill.bill_title,
            label: bill.title || bill.bill_title,
          }));
          setBills(formattedBills);
        } else {
          console.error("Failed to fetch bills");
        }
      } catch (error) {
        console.error("Error fetching bills:", error);
      }
    };

    fetchBills();
  }, []); // Run only once on component mount

  const handleCategoryChange = (selectedOption) => {
    setSelectedCategory(selectedOption);
    if (selectedOption) {
      setSelectedBill(null); // Clear bill selection if category is selected
    }
  };

  const handleBillChange = (selectedOption) => {
    setSelectedBill(selectedOption);
    if (selectedOption) {
      setSelectedCategory(null); // Clear category selection if bill is selected
    }
  };

  const handleClearFilters = () => {
    setSelectedCategory(null);
    setSelectedBill(null);
    setSearchResults(null); // Clear previous search results
    if (onSearchResults) onSearchResults([]); // Clear results in parent
  };

  const handleApplyFilters = async () => {
    try {
      let response;
      if (selectedCategory) {
        response = await axios.get("/api/federal-search", {
          params: { 
            category_name: selectedCategory.value,
            phoneNo: phoneNo
          },
        });
      } else if (selectedBill) {
        response = await axios.get("/api/federal-search", {
          params: { 
            bill_title: selectedBill.value,
            phoneNo: phoneNo
          },
        });
      } else {
        alert("No filters selected.");
        return;
      }

      if (response.status === 200) {
        // Process the response data
        const bills = Array.isArray(response.data) ? response.data : [response.data];
        
        // Add type and ensure vote info exists
        const processedResults = bills.map(bill => ({
          ...bill,
          type: "Federal",
          vote_count: bill.vote_count || 0,
          previous_vote: bill.previous_vote || null
        }));

        setSearchResults(processedResults);
        if (onSearchResults) onSearchResults(processedResults);
      } else {
        setSearchResults(null);
        if (onSearchResults) onSearchResults([]);
        alert("No results found.");
      }

      if (onClose) onClose();
    } catch (error) {
      console.error("Error applying filters:", error);
      alert("Error searching for bills. Please try again.");
    }
  };

  return (
    <div>
      {/* Note Message */}
      <div style={{ marginBottom: "15px" }}>
        <strong>Note:</strong> Select either a Category title or a Bill title from the dropdown.
      </div>

      {/* Search by Category */}
      <div style={{ marginBottom: "15px" }}>
        <label
          htmlFor="category"
          style={{
            display: "block",
            marginBottom: "8px",
            fontWeight: "bold",
            color: "#333",
          }}
        >
          Search or Select a Category:
        </label>
        <Select
          id="category"
          options={categoryOptions}
          value={selectedCategory}
          onChange={handleCategoryChange}
          isDisabled={!!selectedBill} // Disable if a bill is selected
          placeholder="Search or Select a Category"
        />
      </div>

      {/* Search by Bill */}
      <div style={{ marginBottom: "15px" }}>
        <label
          htmlFor="bill"
          style={{
            display: "block",
            marginBottom: "8px",
            fontWeight: "bold",
            color: "#333",
          }}
        >
          Search or Select a Bill Title:
        </label>
        <Select
          id="bill"
          options={bills}
          value={selectedBill}
          onChange={handleBillChange}
          isDisabled={!!selectedCategory} // Disable if a category is selected
          placeholder="Search or Select a Bill Title"
        />
      </div>

      {/* Filter Buttons */}
      <div style={{ 
        display: "flex", 
        justifyContent: "center",
        flexDirection: window.innerWidth <= 926 ? "column" : "row",
        gap: window.innerWidth <= 926 ? "10px" : "20px",
        marginTop: "20px"
      }}>
        <button
          onClick={handleClearFilters}
          style={{
            padding: "8px 20px",
            backgroundColor: "#dc3545",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
            width: window.innerWidth <= 926 ? "100%" : "auto"
          }}
        >
          Clear Search Filters
        </button>
        <button
          onClick={handleApplyFilters}
          style={{
            padding: "8px 20px",
            backgroundColor: "#007bff",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
            width: window.innerWidth <= 926 ? "100%" : "auto"
          }}
        >
          Apply Search Filter
        </button>
      </div>
    </div>
  );
};

export default Search;
